// src/components/MySessions.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, MapPin, Users, XCircle } from 'lucide-react';
import curatedFikaAdminService from '../services/curatedFikaAdminService';
// import mySessionsSessionManager from '../utils/mySessionsSessionManager';

import unifiedSessionManager from '../utils/unifiedSessionManager'; 

const MySessions = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // NEW: Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  // NEW: Extend session on user activity
  useEffect(() => {
    if (step === 'sessions') {
      const activityHandler = () => {
        mySessionsSessionManager.extendSession();
      };

      window.addEventListener('click', activityHandler);
      window.addEventListener('keydown', activityHandler);
      window.addEventListener('scroll', activityHandler);

      return () => {
        window.removeEventListener('click', activityHandler);
        window.removeEventListener('keydown', activityHandler);
        window.removeEventListener('scroll', activityHandler);
      };
    }
  }, [step]);

  // NEW: Check for existing authenticated session
  const origcheckExistingSession = () => {
    if (mySessionsSessionManager.isAuthenticated()) {
      const session = mySessionsSessionManager.getSession();
      
      if (session && session.email && session.sessions) {
        console.log('✅ My Sessions: Existing session found, auto-authenticating user');
        setEmail(session.email);
        setVerificationCode(session.verificationCode || '');
        setSessions(session.sessions);
        setStep('sessions');
        
        const remainingMinutes = mySessionsSessionManager.getRemainingTime();
        console.log(`🔐 My Sessions: Session active for ${remainingMinutes} more minutes`);
        
        // Show warning if session expiring soon
        if (mySessionsSessionManager.needsRefreshWarning()) {
          setSuccessMessage(`Session expires in ${remainingMinutes} minutes. You may need to verify again.`);
          setTimeout(() => setSuccessMessage(''), 5000);
        }
      } else {
        // Invalid session data, clear it
        mySessionsSessionManager.clearSession();
      }
    }
  };

   // UPDATED: Check for existing session on mount
   // 10-17-25
   const checkExistingSession = () => {
    // Check if authenticated in either portal
    if (unifiedSessionManager.isMySessionsAuthenticated()) {
      const sessionData = unifiedSessionManager.getMySessionsData();
      
      if (sessionData && sessionData.email && sessionData.sessions) {
        console.log('✅ My Sessions: Existing session found, auto-authenticating user');
        setEmail(sessionData.email);
        setVerificationCode(sessionData.verificationCode || '');
        setSessions(sessionData.sessions);
        setStep('sessions');
        
        const remainingMinutes = unifiedSessionManager.getRemainingTime();
        console.log(`🔐 My Sessions: Session active for ${remainingMinutes} more minutes`);
        
        if (unifiedSessionManager.needsRefreshWarning()) {
          setSuccessMessage(`Session expires in ${remainingMinutes} minutes. You may need to verify again.`);
          setTimeout(() => setSuccessMessage(''), 5000);
        }
      }
    } else if (unifiedSessionManager.isMemberPortalAuthenticated()) {
      // User authenticated in Member Portal, auto-fill email
      const userData = unifiedSessionManager.getUserApplication();
      if (userData && userData.email) {
        console.log('✅ My Sessions: Found Member Portal session, pre-filling email');
        setEmail(userData.email);
        // Don't auto-authenticate, but save user a step
      }
    }
  };


  // EXISTING: Step 1 handler - ENHANCED with session creation
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await curatedFikaAdminService.sendSessionAccessCode(email);
      
      if (result.success) {
        setSuccessMessage('Verification code sent to your email!');
        setStep('verify');
      } else {
        if (result.error && result.error.includes('No sessions found')) {
          setError("We couldn't find any upcoming sessions for this email address. Make sure you're using the same email you applied with, or check if you have any confirmed sessions.");
        } else {
          setError(result.error || 'Something went wrong. Please try again or contact hello@connectco.app if the issue persists.');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // EXISTING: Step 2 handler - ENHANCED with session creation
  const orighandleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await curatedFikaAdminService.getSessionsByEmailWithCode(email, verificationCode);
      
      if (result.success && result.data) {
        setSessions(result.data);
        setStep('sessions');
        setSuccessMessage('');
        
        // NEW: Create persistent session after successful verification
        mySessionsSessionManager.createSession(email, verificationCode, result.data);
        console.log('✅ My Sessions: Session created and saved');
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 10-17-25
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
  
    try {
      const result = await curatedFikaAdminService.getSessionsByEmailWithCode(email, verificationCode);
      
      if (result.success && result.data) {
        setSessions(result.data);
        setStep('sessions');
        setSuccessMessage('');
        
        // NEW LINE (only change):
        unifiedSessionManager.createMySessionsSession(email, verificationCode, result.data);
        console.log('✅ My Sessions: Session created and saved');
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  // EXISTING: Cancel session handler - ENHANCED with session update
  const orighandleCancelSession = async (participantId, sessionId, sessionDate) => {
    const hoursUntilSession = (new Date(sessionDate) - new Date()) / (1000 * 60 * 60);
    
    if (hoursUntilSession < 24) {
      setError('Cannot cancel within 24 hours of the session');
      setTimeout(() => setError(''), 5000);
      return;
    }
  
    if (!window.confirm('Are you sure you want to cancel your attendance? This cannot be undone.')) {
      return;
    }
  
    setIsLoading(true);
    setError('');
    
    try {
      const result = await curatedFikaAdminService.cancelParticipantAttendance(
        participantId,
        'User requested cancellation via My Sessions page'
      );
      
      if (result.success) {
        setSuccessMessage('Your attendance has been cancelled successfully');
        
        // Refresh sessions
        const refreshResult = await curatedFikaAdminService.getSessionsByEmailWithCode(
          email, 
          verificationCode
        );
        
        if (refreshResult.success && refreshResult.data) {
          setSessions(refreshResult.data);
          
          // NEW: Update session storage with refreshed data
          mySessionsSessionManager.updateSessions(refreshResult.data);
        }
        
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.error || 'Failed to cancel session. Please contact support.');
      }
    } catch (err) {
      console.error('Cancellation error:', err);
      setError('An error occurred. Please try again or contact hello@connectco.app');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSession = async (participantId, sessionId, sessionDate) => {
    const hoursUntilSession = (new Date(sessionDate) - new Date()) / (1000 * 60 * 60);
    
    if (hoursUntilSession < 24) {
      setError('Cannot cancel within 24 hours of the session');
      setTimeout(() => setError(''), 5000);
      return;
    }
  
    if (!window.confirm('Are you sure you want to cancel your attendance? This cannot be undone.')) {
      return;
    }
  
    setIsLoading(true);
    setError('');
    
    try {
      const result = await curatedFikaAdminService.cancelParticipantAttendance(
        participantId,
        'User requested cancellation via My Sessions page'
      );
      
      if (result.success) {
        setSuccessMessage('Your attendance has been cancelled successfully');
        
        const refreshResult = await curatedFikaAdminService.getSessionsByEmailWithCode(
          email, 
          verificationCode
        );
        
        if (refreshResult.success && refreshResult.data) {
          setSessions(refreshResult.data);
          
          // UPDATED: Use unified session manager
          unifiedSessionManager.updateMySessions(refreshResult.data);
        }
        
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.error || 'Failed to cancel session. Please contact support.');
      }
    } catch (err) {
      console.error('Cancellation error:', err);
      setError('An error occurred. Please try again or contact hello@connectco.app');
    } finally {
      setIsLoading(false);
    }

  };

  // NEW: Enhanced back to email handler with session clearing
  const orighandleBackToEmail = () => {
    mySessionsSessionManager.clearSession();
    setStep('email');
    setEmail('');
    setVerificationCode('');
    setSessions([]);
    setError('');
    setSuccessMessage('');
  };

   // UPDATED: Use unified session manager in handleBackToEmail
   // 10-17-25
   const handleBackToEmail = () => {
    unifiedSessionManager.clearSession(); // CHANGED
    setStep('email');
    setEmail('');
    setVerificationCode('');
    setSessions([]);
    setError('');
    setSuccessMessage('');
  };

  // EXISTING: All other handlers remain unchanged
  const getStatusBadge = (status) => {
    const badges = {
      invited: { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Invited', icon: Clock },
      confirmed: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Confirmed', icon: CheckCircle },
      paid: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Confirmed & Paid', icon: CheckCircle },
      attended: { color: 'bg-purple-100 text-purple-800 border-purple-200', text: 'Attended', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Cancelled', icon: XCircle }
    };
    
    const badge = badges[status] || badges.invited;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {badge.text}
      </span>
    );
  };

  const canCancelSession = (session) => {
    if (session.participation_status === 'cancelled' || session.participation_status === 'attended') {
      return false;
    }
    
    const sessionDate = new Date(session.curated_fika_sessions.session_date);
    const hoursUntilSession = (sessionDate - new Date()) / (1000 * 60 * 60);
    
    return hoursUntilSession >= 24;
  };

  // REST OF THE COMPONENT REMAINS EXACTLY THE SAME
  // Just update the "back" buttons to use handleBackToEmail instead of inline logic

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            My Curated Fika Sessions
          </h1>
          <p className="text-lg text-gray-600">
            View and manage your upcoming intimate experiences
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-red-800">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Step 1: Email Entry - UNCHANGED */}
        {step === 'email' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Email</h2>
            <form onSubmit={handleSendCode}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
                <p className="mt-2 text-sm text-gray-500">
                  We'll send a verification code to this email address
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Verification Code - UPDATED back button */}
        {step === 'verify' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Enter Verification Code</h2>
            <p className="text-gray-600 mb-6">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>
            
            <form onSubmit={handleVerifyCode}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  required
                  maxLength={6}
                  pattern="[0-9]{6}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Code expires in 15 minutes
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify & View Sessions'}
              </button>
              
              {/* UPDATED: Use handleBackToEmail */}
              <button
                type="button"
                onClick={handleBackToEmail}
                className="w-full mt-3 text-gray-600 py-2 text-sm hover:text-gray-900"
              >
                ← Back to email entry
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Sessions List - UPDATED back button */}
        {step === 'sessions' && (
          <div>
            {sessions.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">No upcoming sessions found</p>
                <button
                  onClick={handleBackToEmail}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Try another email
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {sessions.map((session) => {
                  const sessionData = session.curated_fika_sessions;
                  const sessionDate = new Date(sessionData.session_date);
                  
                  return (
                    <div key={session.id} className="bg-white rounded-xl shadow-lg p-6">
                      {/* Status Badge */}
                      <div className="mb-4">
                        {getStatusBadge(session.participation_status)}
                      </div>

                      {/* Session Title */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {sessionData.session_title}
                      </h3>

                      {/* Session Details */}
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="flex items-start">
                          <Clock className="w-5 h-5 text-gray-400 mt-1 mr-3 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {sessionDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-gray-600">
                              {sessionDate.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              {sessionData.venue_timezone && ` (${sessionData.venue_timezone})`}
                            </p>
                            <p className="text-sm text-gray-500">
                              Duration: {sessionData.session_duration || 90} minutes
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <MapPin className="w-5 h-5 text-gray-400 mt-1 mr-3 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-gray-900">{sessionData.venue_name}</p>
                            <p className="text-gray-600 text-sm">{sessionData.venue_address}</p>
                            <p className="text-gray-500 text-sm">{sessionData.venue_area}</p>
                          </div>
                        </div>
                      </div>

                      {/* Other Participants */}
                      {session.otherParticipants && session.otherParticipants.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-start">
                            <Users className="w-5 h-5 text-gray-400 mt-1 mr-3 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-gray-900 mb-2">Your Curated Group</p>
                              <div className="flex flex-wrap gap-2">
                                {session.otherParticipants.map((participant, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-800"
                                  >
                                    {participant.firstName} {participant.lastInitial}.
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {canCancelSession(session) && (
                        <div className="pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleCancelSession(session.id, sessionData.id, sessionData.session_date)}
                            className="text-red-600 hover:text-red-700 font-medium text-sm"
                          >
                            Cancel Attendance
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Back Button - UPDATED */}
                <div className="text-center pt-6">
                  <button
                    onClick={handleBackToEmail}
                    className="text-gray-600 hover:text-gray-900 font-medium"
                  >
                    ← View sessions for another email
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySessions;